import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import { FerryCapacityProfileLink } from 'Models/Dtos/CapacityProfileDtos';
/* eslint-disable max-len */

export interface IFerryEntityAttributes extends IModelAttributes {
	name: string;
	helmId: string;
	disabled: boolean;

	additionalBookingSectionss: Array<
		Models.FerriesAdditionalBookingSections | Models.IFerriesAdditionalBookingSectionsAttributes
	>;
	ferryTrips: Array<Models.FerryTripEntity | Models.IFerryTripEntityAttributes>;
	ferryCapacityProfiles: FerryCapacityProfileLink[];
}
@entity('FerryEntity', 'Ferry')
export default class FerryEntity extends Model implements IFerryEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		return {
			path: 'name',
			descending: false,
		};
	}

	@observable
	@attribute<FerryEntity, string>()
	public name: string;

	/**
	 * The capacity profiles this ferry can sail with, default first. Capacity lives on profiles now —
	 * a trip's numbers are its own `capacityProfile`, never the ferry's. Read-only: the links are
	 * written through `PUT /api/admin/transportation/ferries/{id}/capacity-profiles`.
	 */
	@observable
	public ferryCapacityProfiles: FerryCapacityProfileLink[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public additionalBookingSectionss: Models.FerriesAdditionalBookingSections[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public ferryTrips: Models.FerryTripEntity[] = [];

	@observable
	@attribute<FerryEntity, string>()
	public helmId: string;

	/**
	 * Whether the vessel has been taken out of circulation.
	 *
	 * <p>Retired, not deleted — a vessel that has ever run a trip cannot be deleted, because the
	 * trips are the history the reports read. Every picker that commits a vessel to future work
	 * leaves a disabled one out; every filter over what has already happened keeps it. See
	 * <c>selectableFerries</c>.</p>
	 */
	@observable
	@attribute<FerryEntity, boolean>()
	public disabled: boolean;

	constructor(attributes?: Partial<IFerryEntityAttributes>) {
		super(attributes);

		this.additionalBookingSectionss = [];
		this.ferryTrips = [];
		this.ferryCapacityProfiles = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFerryEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.helmId !== undefined) {
				this.helmId = attributes.helmId;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (
				attributes.additionalBookingSectionss !== undefined &&
				Array.isArray(attributes.additionalBookingSectionss)
			) {
				if (!this.additionalBookingSectionss) {
					this.additionalBookingSectionss = [];
				}
				for (const model of attributes.additionalBookingSectionss) {
					if (model instanceof Models.FerriesAdditionalBookingSections) {
						this.additionalBookingSectionss.push(model);
					} else {
						this.additionalBookingSectionss.push(new Models.FerriesAdditionalBookingSections(model));
					}
				}
			}
			if (attributes.ferryTrips !== undefined && Array.isArray(attributes.ferryTrips)) {
				if (!this.ferryTrips) {
					this.ferryTrips = [];
				}
				for (const model of attributes.ferryTrips) {
					if (model instanceof Models.FerryTripEntity) {
						this.ferryTrips.push(model);
					} else {
						this.ferryTrips.push(new Models.FerryTripEntity(model));
					}
				}
			}
			if (attributes.ferryCapacityProfiles !== undefined && Array.isArray(attributes.ferryCapacityProfiles)) {
				this.ferryCapacityProfiles = attributes.ferryCapacityProfiles;
			}
		}
	}
}
